import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { OptJobStatus, OptJobStatusObject } from 'api/optJobs/optJobs.api'
import { QueueItemStatus } from 'api/queueItems/queueItems.types'
import { Setting } from 'api/settings/settings.api'
import { getMomentFromCurrentFullHour } from 'helpers/dateTime.helper/dateTime.helper'
import i18n from 'i18next'
import moment, { Moment } from 'moment'

export const timeCategoryField = `time`
export const nonStackedIndex = 10000

// Default optimization horizon when the system has no default_optimization_period: 1 week minus 1 hour (7 * 24 - 1).
export const DEFAULT_OPTIMIZATION_PERIOD_HOURS = 167
export const DISABLED_SUBTYPE_ALIAS_VALUE = 'disabled_subtype'

export type SubtypeAliasOption = {
  key: string
  value: string
  displayName: string
}

type PartitionAliasOption = Pick<SubtypeAliasOption, 'key' | 'displayName'>

function normalizeDynamicAliasKey(key: string): string | null {
  const normalizedKey = key
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')

  return normalizedKey || null
}

function getDynamicAliasKey(namespace: string, key: string): string | null {
  const normalizedKey = normalizeDynamicAliasKey(key)
  return normalizedKey ? `${namespace}_${normalizedKey}` : null
}

export function hasDynamicAliasKeyCollision(keys: string[]): boolean {
  const aliasKeys = new Set<string>()

  for (const key of keys) {
    const aliasKey = normalizeDynamicAliasKey(key)
    if (!aliasKey) {
      continue
    }
    if (aliasKeys.has(aliasKey)) {
      return true
    }
    aliasKeys.add(aliasKey)
  }

  return false
}

function buildDynamicSelectionAliases<TOption extends { key: string }>({
  options,
  selectedKey,
  namespace,
  getActiveValue,
}: {
  options: TOption[]
  selectedKey: string
  namespace: string
  getActiveValue: (option: TOption) => string
}): UiConfigAliases {
  if (hasDynamicAliasKeyCollision(options.map(({ key }) => key))) {
    throw new Error('Dynamic UI config alias keys must be unique')
  }

  return options.reduce<UiConfigAliases>((aliases, option) => {
    const aliasKey = getDynamicAliasKey(namespace, option.key)
    if (aliasKey) {
      aliases[aliasKey] = option.key === selectedKey ? getActiveValue(option) : DISABLED_SUBTYPE_ALIAS_VALUE
    }
    return aliases
  }, {})
}

/**
 * Partition aliases carry semantic selection identity rather than execution identity.
 * This lets UiConfig opt into a configured key-to-regular-subtype relationship without
 * teaching application code that partitions are universally equivalent to subtypes.
 */
export function buildPartitionAliases({
  options,
  selectedKey,
}: {
  options: PartitionAliasOption[]
  selectedKey: string
}): UiConfigAliases {
  const selectedOption = options.find((option) => option.key === selectedKey)

  return {
    selected_partition_key: selectedOption?.key ?? null,
    selected_partition_display_name: selectedOption?.displayName ?? null,
    ...buildDynamicSelectionAliases({
      options,
      selectedKey,
      namespace: 'partition',
      getActiveValue: (option) => option.key,
    }),
  }
}

export function buildSubtypeAliases({
  options,
  selectedKey,
}: {
  options: SubtypeAliasOption[]
  selectedKey: string
}): UiConfigAliases {
  const selectedOption = options.find((option) => option.key === selectedKey)

  return {
    selected_subtype: selectedOption?.value ?? null,
    selected_subtype_display_name: selectedOption?.displayName ?? null,
    ...buildDynamicSelectionAliases({
      options,
      selectedKey,
      namespace: 'subtype',
      getActiveValue: (option) => option.value,
    }),
  }
}

export function getOptimizationPeriod(): {
  startTime: Moment
  endTime: Moment
} {
  const startTime = moment().add(1, `hour`).startOf(`hour`)
  return {
    startTime: startTime,
    endTime: moment(startTime).add(DEFAULT_OPTIMIZATION_PERIOD_HOURS, `hours`),
  }
}

export function isActiveSetting(setting: Setting | DigitalTwinSetting): boolean {
  const optStartTime = moment().add(1, `hour`).set(`minutes`, 0).set(`seconds`, 0)
  return optStartTime <= moment(setting.end_time) || setting.end_time === null
}

export function isHistorySetting(setting: Setting | DigitalTwinSetting): boolean {
  const optStartTime = moment().add(1, `hour`).set(`minutes`, 0).set(`seconds`, 0)
  return moment(setting.end_time) < optStartTime
}

export function getDelay(): number {
  const now = moment()
  const nextHour = getMomentFromCurrentFullHour(1)
  return nextHour.diff(now)
}

export function optJobIsDone(optJobStatus?: OptJobStatusObject | null): boolean {
  if (!optJobStatus) {
    return true
  }

  return optJobStatus.status !== `Created` && optJobStatus.status !== `Queued` && optJobStatus.status !== `Running`
}

/**
 * Why an optimization has nothing to show, for the two statuses that end it without a result.
 * `Failed` means the solve broke down on its way, which is ours to look into. `Unsolvable`
 * means it ran to completion and found no feasible plan, which is a statement about input the
 * user controls — telling them to contact support would send them to the wrong place.
 *
 * Returns a translation key rather than translated text, so the callers that already translate
 * (`StatusIcon`, the phase matrix) stay the only place a locale is resolved. Every other status
 * returns undefined: where a job is on its way is already the whole of what the icon says.
 */
export function getOptimizationFailureExplanationKey(status: OptJobStatus | QueueItemStatus): string | undefined {
  if (status === `Unsolvable`) {
    return `The optimization is unsolvable because of infeasible settings, meas values or forecasts.`
  }
  if (status === `Failed`) {
    return `Optimization failed. Please try again or contact support.`
  }

  return undefined
}

export function getTranslatedCategoryDisplayName(category: string | null): string {
  return ({
    Grundvärde: i18n.t(`Base value`),
    Gränsvärde: i18n.t(`Limit value`),
    Tillgänglighet: i18n.t(`Availability`),
    Tvångskörning: i18n.t(`Forced availability`),
  }[category || ``] ?? `-`) as string
}
